import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Header, Table } from 'semantic-ui-react'
import styled from 'styled-components'
import { CoolFarmDairyResultsType } from '~/api/services/coolFarm'
import { formatNumber } from '~/utils/helpers'

interface DetailedDataTableProps {
  results: CoolFarmDairyResultsType
}

const Segment = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin: 20px 0;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
`

const HideDataLink = styled.a`
  color: #007bff;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const safeParseFloat = (value: string | number | undefined): number => {
  if (value === undefined || value === null) return 0
  return parseFloat(String(value))
}

const formatChartNumber = (num: number) => {
  if (num >= 1000000) {
    return formatNumber(num / 1000000, 2) + 'M'
  } else if (num >= 1000) {
    return formatNumber(num / 1000, 2) + 'k'
  }
  return formatNumber(num, 2)
}

const DetailedDataTable: React.FC<DetailedDataTableProps> = ({ results }) => {
  const { t } = useTranslation()
  const [showDetailedData, setShowDetailedData] = useState(true)

  const chartData = useMemo(() => {
    return [
      {
        name: t('Energy'),
        co2: safeParseFloat(results.energyCo2Emission),
        n2o: safeParseFloat(results.energyN2oEmission),
        ch4: safeParseFloat(results.energyCh4Emission),
        total: safeParseFloat(results.energyTotalEmission),
        perFpcm: safeParseFloat(results.energyEmissionPerFpcm)
      },
      {
        name: t('Enteric fermentation'),
        co2: safeParseFloat(results.entericCo2Emission),
        n2o: safeParseFloat(results.entericN2oEmission),
        ch4: safeParseFloat(results.entericCh4Emission),
        total: safeParseFloat(results.entericTotalEmission),
        perFpcm: safeParseFloat(results.entericEmissionPerFpcm)
      },
      {
        name: t('Feed production'),
        co2: safeParseFloat(results.feedCo2Emission),
        n2o: safeParseFloat(results.feedN2oEmission),
        ch4: safeParseFloat(results.feedCh4Emission),
        total: safeParseFloat(results.feedTotalEmission),
        perFpcm: safeParseFloat(results.feedEmissionPerFpcm)
      },
      {
        name: t('Fertilizer'),
        co2: safeParseFloat(results.fertilizerCo2Emission),
        n2o: safeParseFloat(results.fertilizerN2oEmission),
        ch4: safeParseFloat(results.fertilizerCh4Emission),
        total: safeParseFloat(results.fertilizerTotalEmission),
        perFpcm: safeParseFloat(results.fertilizerEmissionPerFpcm)
      },
      {
        name: t('Grazing'),
        co2: safeParseFloat(results.grazingCo2Emission),
        n2o: safeParseFloat(results.grazingN2oEmission),
        ch4: safeParseFloat(results.grazingCh4Emission),
        total: safeParseFloat(results.grazingTotalEmission),
        perFpcm: safeParseFloat(results.grazingEmissionPerFpcm)
      },
      {
        name: t('Manure management'),
        co2: safeParseFloat(results.manureCo2Emission),
        n2o: safeParseFloat(results.manureN2oEmission),
        ch4: safeParseFloat(results.manureCh4Emission),
        total: safeParseFloat(results.manureTotalEmission),
        perFpcm: safeParseFloat(results.manureEmissionPerFpcm)
      },
      {
        name: t('Transportation'),
        co2: safeParseFloat(results.transportCo2Emission),
        n2o: safeParseFloat(results.transportN2oEmission),
        ch4: safeParseFloat(results.transportCh4Emission),
        total: safeParseFloat(results.transportTotalEmission),
        perFpcm: safeParseFloat(results.transportEmissionPerFpcm)
      }
    ]
  }, [results, t])

  return (
    <Segment>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <Header as='h3'>
          {t('Detailed data (all values in kg)')}
        </Header>
        <HideDataLink onClick={() => setShowDetailedData(!showDetailedData)}>
          {showDetailedData ? t('Hide data') : t('Show data')}
        </HideDataLink>
      </div>

      {showDetailedData && (
        <Table celled style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell style={{ background: '#f8f9fa', fontWeight: '600' }}>
                {t('Sources')}
              </Table.HeaderCell>
              <Table.HeaderCell style={{ background: '#f8f9fa', fontWeight: '600' }}>
                <div className='unit' dangerouslySetInnerHTML={{ __html: '<span>kg CO<sub>2</sub></span>' }}/>
              </Table.HeaderCell>
              <Table.HeaderCell style={{ background: '#f8f9fa', fontWeight: '600' }}>
                <div className='unit' dangerouslySetInnerHTML={{ __html: '<span>kg N<sub>2</sub>O</span>' }}/>
              </Table.HeaderCell>
              <Table.HeaderCell style={{ background: '#f8f9fa', fontWeight: '600' }}>
                <div className='unit' dangerouslySetInnerHTML={{ __html: '<span>kg CH<sub>4</sub></span>' }}/>
              </Table.HeaderCell>
              <Table.HeaderCell style={{ background: '#f8f9fa', fontWeight: '600' }}>
                <div className='unit' dangerouslySetInnerHTML={{ __html: '<span>kg Total CO<sub>2</sub>eq</span>' }}/>
              </Table.HeaderCell>
              <Table.HeaderCell style={{ background: '#f8f9fa', fontWeight: '600' }}>
                <div className='unit' dangerouslySetInnerHTML={{ __html: '<span>Per kg FPCM</span>' }}/>
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {chartData.map((item, index) => (
              <Table.Row key={index}>
                <Table.Cell style={{ borderTop: '1px solid #e9ecef' }}>
                  {item.name}
                </Table.Cell>
                <Table.Cell style={{ borderTop: '1px solid #e9ecef' }}>
                  {formatChartNumber(item.co2)}
                </Table.Cell>
                <Table.Cell style={{ borderTop: '1px solid #e9ecef' }}>
                  {formatChartNumber(item.n2o)}
                </Table.Cell>
                <Table.Cell style={{ borderTop: '1px solid #e9ecef' }}>
                  {formatChartNumber(item.ch4)}
                </Table.Cell>
                <Table.Cell style={{ borderTop: '1px solid #e9ecef' }}>
                  {formatChartNumber(item.total)}
                </Table.Cell>
                <Table.Cell style={{ borderTop: '1px solid #e9ecef' }}>
                  {formatChartNumber(item.perFpcm)}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </Segment>
  )
}

export default DetailedDataTable
